import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import React, { useEffect, useRef } from 'react';
import { useEffectEvent } from 'use-effect-event';
import * as styles from './PostModalView.module.scss';
import PostModalViewMagneticWall from './PostModalViewMagneticWall';
import PostModalViewTopicBar from './PostModalViewTopicBar';
import Button from '../../components/Button';
import ScrollArea from '../../components/ScrollArea';
import SVGIcon from '../../components/SVGIcon';
import Timestamp from '../../components/Timestamp';
import { AssetTokenProvider } from '../../editor/AssetTokenContext';
import QuillComponent from '../../editor/QuillComponent';
import { useQuill, withQuillContextProvider } from '../../editor/QuillContext';
import useHeaderCollapse from '../../editor/useHeaderCollapse';
import { gql } from '../../graphql/generated';
import resizeHandlerIcon from '../../icons/extension/resize-handler.svg';
import clearIcon from '../../icons/non-prefilled/16x16/clear.svg';
import collapseIcon from '../../icons/non-prefilled/16x16/collapse.svg';
import expandIcon from '../../icons/non-prefilled/16x16/expand.svg';
import newTabIcon from '../../icons/non-prefilled/16x16/newTab.svg';
import { LinkForceNewTabContext } from '../../utils/router/Link';
import postPath from '../../utils/url/postPath';
import ControlBarInsightsButton from '../controlBar/ControlBarInsightsButton';

const POST_QUERY = gql(/** @complexity 23 */ `
  query PostModalViewPost(
    $id: ID!
  ) {
    post(
      id: $id
    ) {
      id
      content {
        id
        delta
      }
      banner {
        original
      }
      updatedAt
      externalPost {
        id
      }
      ...ControlBarInsightsButton_post
      ...PostModalViewTopicBar_post
      ...PostModalViewMagneticWall_post
      ...postPath_post
    }
    entityAssetToken(entityId: $id, entityType: POST) {
      ...AssetTokenProvider_entityAssetToken
    }
  }
`);

interface PostModalViewProps {
  id: string;
  expanded?: boolean;
  onExpandChange?: (expanded: boolean) => void;
  onClose: () => void;
  onDraggingStart?: (position: [number, number]) => void;
  onDraggingMove?: (position: [number, number]) => void;
  onDraggingEnd?: () => void;
  onResizeStart?: (position: [number, number]) => void;
  onResizeMove?: (position: [number, number]) => void;
  onResizeEnd?: () => void;
}

const PostModalView = withQuillContextProvider(
  ({
    id,
    expanded,
    onExpandChange,
    onClose,
    onDraggingStart,
    onDraggingMove,
    onDraggingEnd,
    onResizeStart,
    onResizeMove,
    onResizeEnd,
  }: PostModalViewProps) => {
    const { data } = useQuery(POST_QUERY, { variables: { id } });

    const draggingStateRef = useRef<{
      capturedElement: Element;
      hasCaptured: boolean;
    } | null>(null);
    const handleStartDragging = (event: React.PointerEvent) => {
      if (!(event.target instanceof Element)) return;

      if (window.getComputedStyle(event.target).cursor !== 'move') return;

      draggingStateRef.current = {
        capturedElement: event.target,
        hasCaptured: false,
      };
      onDraggingStart?.([event.screenX, event.screenY]);
    };

    const handleEndDragging = (event: React.PointerEvent) => {
      if (!draggingStateRef.current) return;

      if (draggingStateRef.current.hasCaptured) {
        draggingStateRef.current.capturedElement.releasePointerCapture(
          event.pointerId,
        );
      }
      draggingStateRef.current = null;
      onDraggingEnd?.();
    };

    const handleDragging = (event: React.PointerEvent) => {
      if (!draggingStateRef.current) return;

      if (!draggingStateRef.current.hasCaptured) {
        draggingStateRef.current.hasCaptured = true;
        draggingStateRef.current.capturedElement.setPointerCapture(
          event.pointerId,
        );
      }

      onDraggingMove?.([event.screenX, event.screenY]);
    };

    const resizingStateRef = useRef<{ capturedElement: Element } | null>(null);
    const handleStartResizing = (event: React.PointerEvent) => {
      if (!(event.target instanceof Element)) return;

      resizingStateRef.current = { capturedElement: event.target };
      event.target.setPointerCapture(event.pointerId);
      onResizeStart?.([event.screenX, event.screenY]);
    };

    const handleEndResizing = (event: React.PointerEvent) => {
      if (!resizingStateRef.current) return;

      resizingStateRef.current.capturedElement.releasePointerCapture(
        event.pointerId,
      );
      resizingStateRef.current = null;
      onResizeEnd?.();
    };

    const handleResizing = (event: React.PointerEvent) => {
      if (!resizingStateRef.current) return;

      onResizeMove?.([event.screenX, event.screenY]);
    };

    const { quill } = useQuill();
    useHeaderCollapse(quill);

    const shouldHandleEscape = expanded && !!onExpandChange;
    const onEscapePressed = useEffectEvent(() => onExpandChange?.(false));
    useEffect(() => {
      if (!shouldHandleEscape) return undefined;

      const listener = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          onEscapePressed();
        }
      };

      document.body.addEventListener('keydown', listener);
      return () => {
        document.body.removeEventListener('keydown', listener);
      };
    }, [shouldHandleEscape]);

    return data ? (
      <LinkForceNewTabContext.Provider value>
        <div className={styles.container}>
          <ScrollArea className={styles.scrollArea}>
            <div
              className={classNames(styles.contentContainer, {
                [styles.isMoveable]: !!onDraggingStart,
              })}
              onPointerMove={onDraggingMove && handleDragging}
              onPointerDown={onDraggingStart && handleStartDragging}
              onPointerLeave={onDraggingEnd && handleEndDragging}
              onPointerUp={onDraggingEnd && handleEndDragging}
            >
              {data.post.banner.original ? (
                <div className={styles.bannerContainer}>
                  <img
                    className={styles.banner}
                    draggable={false}
                    src={data.post.banner.original}
                    alt=""
                  />
                </div>
              ) : null}
              <div className={styles.headerControls}>
                <div className={styles.controlBarContainer}>
                  <ControlBarInsightsButton
                    post={data.post}
                    popOverPlacement="bottom-start"
                  >
                    {data.post.externalPost ? 'Synced' : 'Edited'}{' '}
                    <Timestamp date={data.post.updatedAt} />
                  </ControlBarInsightsButton>
                </div>
                <div className={styles.topicBarContainer}>
                  <PostModalViewTopicBar post={data.post} />
                </div>
              </div>
              <div className={styles.content}>
                <div className={styles.editorContainer}>
                  <AssetTokenProvider entityAssetToken={data.entityAssetToken}>
                    <QuillComponent
                      initialContent={data.post.content.delta}
                      rootClassName={styles.editorRoot}
                      contentPlaceholder=""
                      namespace="extensionPostCard"
                      readOnly
                      titlePlaceholder="Untitled"
                    />
                  </AssetTokenProvider>
                </div>
              </div>
              <div className={styles.padding} />
              <div className={styles.magneticWallContainer}>
                {quill && (
                  <PostModalViewMagneticWall quill={quill} post={data.post} />
                )}
              </div>
            </div>
          </ScrollArea>
          {onResizeStart && (
            <button
              type="button"
              className={styles.resizeHandler}
              onPointerDown={handleStartResizing}
              onPointerUp={onResizeEnd && handleEndResizing}
              onPointerLeave={onResizeEnd && handleEndResizing}
              onPointerMove={onResizeMove && handleResizing}
            >
              <SVGIcon>{resizeHandlerIcon}</SVGIcon>
            </button>
          )}
          <div className={styles.buttonGroup}>
            <Button
              size="small"
              className={styles.button}
              onClick={() => onClose()}
            >
              <SVGIcon size={14}>{clearIcon}</SVGIcon>
            </Button>
            {onExpandChange && (
              <Button
                size="small"
                className={styles.button}
                onClick={() => {
                  onExpandChange(!expanded);
                }}
              >
                <SVGIcon size={14}>
                  {expanded ? collapseIcon : expandIcon}
                </SVGIcon>
              </Button>
            )}
            <Button
              size="small"
              to={postPath(data.post)}
              className={styles.button}
            >
              <SVGIcon size={14}>{newTabIcon}</SVGIcon>
            </Button>
          </div>
        </div>
      </LinkForceNewTabContext.Provider>
    ) : null;
  },
);

type PostModalViewModalProps = Omit<PostModalViewProps, 'onClose'> & {
  close: () => void;
};

export const PostModalViewModal = ({
  close,
  ...props
}: PostModalViewModalProps) => (
  <div className={styles.modal}>
    <PostModalView {...props} onClose={close} />
  </div>
);

export default PostModalView;
